import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { type InkTone, inkToneVariants } from '@lite-workspace/shared/helm-ui/spinner';
import { classes } from '@lite-workspace/shared/helm-ui/utils';
import { catchError, defer, from, of, switchMap } from 'rxjs';

const dataUriCache = new Map<string, string>();

async function loadIconDataUri(src: string): Promise<string | null> {
  const response = await fetch(src);
  if (!response.ok) return null;
  const svg = await response.text();
  const uri = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  dataUriCache.set(src, uri);
  return uri;
}

@Component({
  selector: 'hlm-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
  host: {
    role: 'img',
    class: 'inline-block shrink-0 align-[var(--align-icon-optical)]',
    '[class.bg-current]': 'mask() !== null',
    '[style.width.px]': 'size()',
    '[style.height.px]': 'size()',
    '[style.-webkit-mask]': 'mask()',
    '[style.mask]': 'mask()',
  },
})
export class HlmIconComponent {
  readonly src = input.required<string>();
  readonly size = input(16);
  readonly tone = input<InkTone>('inherit');

  // Safari drops CSS masks whose image is an external file that loads late; inline the
  // svg as a data-URI so the mask never depends on a separate resource load.
  private readonly dataUri = toSignal(
    toObservable(this.src).pipe(
      switchMap(src => {
        const cached = dataUriCache.get(src);
        if (cached) return of(cached);
        return defer(() => from(loadIconDataUri(src))).pipe(catchError(() => of(null)));
      }),
    ),
    { initialValue: null },
  );

  protected readonly mask = computed(() => {
    const uri = this.dataUri();
    return uri ? `url("${uri}") center / contain no-repeat` : null;
  });

  constructor() {
    classes(() => inkToneVariants({ tone: this.tone() }));
  }
}
